import React, { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import ChevronLeft from "../Icons/ChevronLeft";
import ChevronRight from "../Icons/ChevronRight";

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

type SecurePdfViewerProps = {
  pdfBlob: Blob | null;
  loading?: boolean;
  className?: string;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
  layout?: "default" | "modal";
  pageLabel?: string;
};

export default function SecurePdfViewer({
  pdfBlob,
  loading = false,
  className = "",
  loadingMessage = "Loading presentation...",
  errorMessage = "Unable to render presentation.",
  emptyMessage = "No presentation available.",
  layout = "default",
  pageLabel = "Slide",
}: SecurePdfViewerProps): React.ReactElement {
  const isModalLayout = layout === "modal";
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderRequestRef = useRef(0);
  const [parsing, setParsing] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const renderPage = useCallback(async (pageNum: number) => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!pdf || !canvas || !container) {
      return;
    }

    renderTaskRef.current?.cancel();
    const requestId = ++renderRequestRef.current;

    setRendering(true);
    try {
      const page = await pdf.getPage(pageNum);
      if (requestId !== renderRequestRef.current) {
        return;
      }

      const containerWidth = container.clientWidth || 960;
      const pixelRatio = window.devicePixelRatio || 1;
      const baseViewport = page.getViewport({ scale: 1, rotation: page.rotate });
      const displayScale = Math.min(2.5, containerWidth / baseViewport.width);
      const viewport = page.getViewport({
        scale: displayScale * pixelRatio,
        rotation: page.rotate,
      });
      const context = canvas.getContext("2d");
      if (!context) {
        return;
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / pixelRatio}px`;
      canvas.style.height = `${viewport.height / pixelRatio}px`;

      context.clearRect(0, 0, canvas.width, canvas.height);

      const renderTask = page.render({
        canvasContext: context,
        viewport,
      });
      renderTaskRef.current = renderTask;

      await renderTask.promise;
    } catch (renderError) {
      if (
        typeof renderError === "object" &&
        renderError !== null &&
        "name" in renderError &&
        (renderError as { name?: string }).name === "RenderingCancelledException"
      ) {
        return;
      }
    } finally {
      if (requestId === renderRequestRef.current) {
        renderTaskRef.current = null;
        setRendering(false);
      }
    }
  }, []);

  useEffect(() => {
    const blockShortcuts = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        ["s", "p", "c", "a"].includes(event.key.toLowerCase())
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", blockShortcuts);
    return () => document.removeEventListener("keydown", blockShortcuts);
  }, []);

  useEffect(() => {
    if (!pdfBlob) {
      pdfRef.current = null;
      setTotalPages(0);
      setCurrentPage(1);
      setError("");
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setParsing(true);
      setError("");
      try {
        const data = await pdfBlob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) {
          void pdf.destroy();
          return;
        }
        pdfRef.current = pdf;
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
      } catch {
        if (!cancelled) {
          setError(errorMessage);
          pdfRef.current = null;
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) {
          setParsing(false);
        }
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      if (pdfRef.current) {
        void pdfRef.current.destroy();
        pdfRef.current = null;
      }
    };
  }, [pdfBlob, errorMessage]);

  useEffect(() => {
    if (!pdfRef.current || totalPages === 0) {
      return;
    }
    void renderPage(currentPage);
  }, [currentPage, totalPages, renderPage]);

  useEffect(() => {
    if (!isModalLayout || totalPages === 0) {
      return;
    }

    let resizeTimer: number | undefined;

    const handleResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        void renderPage(currentPage);
      }, 250);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(resizeTimer);
    };
  }, [currentPage, isModalLayout, renderPage, totalPages]);

  useEffect(() => {
    if (isModalLayout || totalPages === 0) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    let resizeTimer: number | undefined;

    const handleResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        void renderPage(currentPage);
      }, 250);
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(container);

    return () => {
      observer.disconnect();
      window.clearTimeout(resizeTimer);
    };
  }, [currentPage, isModalLayout, renderPage, totalPages]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        setCurrentPage((page) => Math.min(totalPages, page + 1));
      }
      if (event.key === "ArrowLeft") {
        setCurrentPage((page) => Math.max(1, page - 1));
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [totalPages]);

  const showLoading = loading || parsing || rendering;

  if (!pdfBlob && !showLoading) {
    return (
      <div
        className={`flex items-center justify-center text-sm text-gray-font ${
          isModalLayout ? "min-h-[20rem] h-full" : "min-h-[32rem]"
        } ${className}`}
      >
        {emptyMessage}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex items-center justify-center px-4 text-center text-sm text-red-600 ${
          isModalLayout ? "min-h-[20rem] h-full" : "min-h-[32rem]"
        } ${className}`}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-col select-none ${
        isModalLayout ? "h-full min-h-0 bg-[#EEF2F6]" : "bg-[#F6F6F6]"
      } ${className}`}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div
        ref={containerRef}
        className={`relative flex justify-center ${
          isModalLayout
            ? "min-h-0 flex-1 overflow-y-auto px-6 py-8"
            : "min-h-[32rem] items-center p-4"
        }`}
        onContextMenu={(event) => event.preventDefault()}
      >
        {showLoading ? (
          <div
            className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 text-sm text-gray-font ${
              isModalLayout ? "bg-[#EEF2F6]/95" : "bg-[#F6F6F6]/90"
            }`}
          >
            {isModalLayout ? (
              <span
                className="secure-pdf-viewer-spinner"
                aria-hidden="true"
              />
            ) : null}
            {loadingMessage}
          </div>
        ) : null}
        <canvas
          ref={canvasRef}
          className={`mx-auto block max-w-full select-none pointer-events-none bg-white ${
            isModalLayout
              ? "rounded-xl shadow-[0_10px_40px_rgba(15,17,20,0.14)] ring-1 ring-black/5"
              : "shadow-md"
          }`}
          draggable={false}
          onContextMenu={(event) => event.preventDefault()}
        />
      </div>

      {totalPages > 0 && (
        <div
          className={`flex items-center justify-center gap-4 bg-white px-4 py-3 ${
            isModalLayout
              ? "border-t border-[#D8DEE8] shadow-[0_-4px_18px_rgba(15,17,20,0.06)]"
              : "gap-6 border-t border-gray-200"
          }`}
        >
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage <= 1 || showLoading}
            className={`inline-flex items-center gap-2 rounded-md font-Roboto text-sm font-semibold text-[#004578] transition-opacity disabled:cursor-not-allowed disabled:opacity-40 ${
              isModalLayout
                ? "border border-[#C5D0DC] bg-white px-3 py-2 hover:bg-[#F3F7FB]"
                : "px-4 py-2"
            }`}
            aria-label="Previous page"
          >
            {isModalLayout ? <ChevronLeft className="h-4 w-4" /> : null}
            Previous
          </button>
          <span className="min-w-[7.5rem] text-center font-Roboto text-sm text-gray-font">
            {pageLabel} {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={currentPage >= totalPages || showLoading}
            className={`inline-flex items-center gap-2 rounded-md font-Roboto text-sm font-semibold text-[#004578] transition-opacity disabled:cursor-not-allowed disabled:opacity-40 ${
              isModalLayout
                ? "border border-[#C5D0DC] bg-white px-3 py-2 hover:bg-[#F3F7FB]"
                : "px-4 py-2"
            }`}
            aria-label="Next page"
          >
            Next
            {isModalLayout ? <ChevronRight className="h-4 w-4" /> : null}
          </button>
        </div>
      )}
    </div>
  );
}
